import React, { useState, useCallback, useEffect } from 'react';

// Composant de notification corrigé
const ModernNotification = ({
                                id,
                                type = 'info',
                                message,
                                title,
                                duration = 5000,
                                autoClose = true,
                                onClose,
                                onClick,
                                minimizeOnExpire = false // Nouveau prop
                            }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false); // Nouvel état local
    const [progress, setProgress] = useState(100);

    const handleClose = useCallback((e) => {
        if (e) e.stopPropagation();
        setIsExiting(true);
        setTimeout(() => {
            onClose && onClose(id);
        }, 700);
    }, [id, onClose]);

    useEffect(() => {
        // Animation d'entrée
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (autoClose && duration > 0 && !isMinimized) {
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    const newProgress = prev - (100 / (duration / 100));
                    return newProgress <= 0 ? 0 : newProgress;
                });
            }, 100);

            const expireTimer = setTimeout(() => {
                if (minimizeOnExpire) {
                    setIsMinimized(true);
                } else {
                    handleClose();
                }
            }, duration);

            return () => {
                clearInterval(progressInterval);
                clearTimeout(expireTimer);
            };
        }
    }, [duration, autoClose, handleClose, minimizeOnExpire, isMinimized]);

    const getNotificationConfig = (type) => {
        const configs = {
            success: {
                icon: 'ri-checkbox-circle-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            error: {
                icon: 'ri-close-circle-fill',
                bgColor: 'bg-red-500',
                borderColor: 'border-red-100',
                textColor: 'text-gray-800',
                titleColor: 'text-red-600',
                progressColor: 'bg-red-500',
                shadowColor: 'shadow-red-500/10'
            },
            warning: {
                icon: 'ri-alert-fill',
                bgColor: 'bg-[#FFD700]',
                borderColor: 'border-[#FFD700]',
                textColor: 'text-gray-800',
                titleColor: 'text-gray-900',
                progressColor: 'bg-[#FFD700]',
                shadowColor: 'shadow-[#FFD700]/10'
            },
            info: {
                icon: 'ri-information-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            love: {
                icon: 'ri-heart-fill',
                bgColor: 'bg-[#264D3F]',
                borderColor: 'border-[#264D3F]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-[#264D3F]',
                shadowColor: 'shadow-[#264D3F]/10'
            },
            premium: {
                icon: 'ri-vip-crown-fill',
                bgColor: 'bg-gradient-to-r from-[#264D3F] to-[#FFD700]',
                borderColor: 'border-[#FFD700]',
                textColor: 'text-gray-800',
                titleColor: 'text-[#264D3F]',
                progressColor: 'bg-gradient-to-r from-[#264D3F] to-[#FFD700]',
                shadowColor: 'shadow-[#FFD700]/10'
            }
        };

        return configs[type] || configs.info;
    };

    const config = getNotificationConfig(type);

    return (
        <div
            className={`
                ${isMinimized ? 'w-16' : 'w-96'} max-w-[calc(100vw-2rem)] mb-3
                transform transition-all duration-700 ease-in-out
                ${isVisible && !isExiting
                ? 'translate-x-0 opacity-100 scale-100'
                : 'translate-x-full opacity-0 scale-95'
            }
            `}
        >
            <div
                className={`
                    relative overflow-hidden ${isMinimized ? 'rounded-full h-16 w-16 p-0 group ring-4 ring-white shadow-2xl' : 'rounded-xl p-4 border-2 p-4'} 
                    backdrop-blur-sm bg-white shadow-lg ${config.borderColor} ${config.shadowColor}
                    hover:shadow-xl transition-all duration-500
                    ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''}
                `}
                onClick={() => onClick && onClick()}
            >
                {/* Barre de progression - Uniquement en mode normal */}
                {!isMinimized && autoClose && duration > 0 && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gray-200">
                        <div
                            className={`h-full transition-all duration-100 ${config.progressColor}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                {/* Contenu principal */}
                {isMinimized ? (
                    <div className="w-full h-full flex items-center justify-center relative overflow-hidden">
                        <div className={`w-full h-full flex items-center justify-center ${config.bgColor}`}>
                             <i className={`${config.icon} text-2xl text-white`} />
                        </div>
                        {/* Indicateur de nouveau message pulsant */}
                        <div className="absolute inset-0 bg-white/20 animate-ping rounded-full pointer-events-none"></div>
                        
                        {/* Petit bouton de suppression sur la bulle au survol (mobile friendly) */}
                        <button 
                            onClick={handleClose}
                            className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg"
                        >
                            <i className="ri-close-line text-xs"></i>
                        </button>
                    </div>
                ) : (
                    <div className="flex items-start gap-3">
                        {/* Icône */}
                        <div className={`
                            flex-shrink-0 w-10 h-10 rounded-lg ${config.bgColor} 
                            flex items-center justify-center shadow-md
                        `}>
                            <i className={`${config.icon} text-xl text-white`} />
                        </div>

                        {/* Contenu textuel */}
                        <div className="flex-1 min-w-0">
                            {title && (
                                <h4 className={`
                                    text-base font-semibold mb-1 ${config.titleColor}
                                    flex items-center gap-2
                                `}>
                                    {title}
                                    {type === 'premium' && (
                                        <i className="ri-sparkling-2-fill text-yellow-500 text-xs" />
                                    )}
                                </h4>
                            )}

                            <p className={`
                                text-sm ${config.textColor}
                                ${!title ? 'font-medium' : ''}
                            `}>
                                {message}
                            </p>
                        </div>

                        {/* Bouton de fermeture */}
                        <button
                            onClick={handleClose}
                            className={`
                                flex-shrink-0 w-6 h-6 rounded-md 
                                flex items-center justify-center
                                hover:bg-gray-100 transition-colors duration-200
                                ${config.textColor} hover:opacity-80
                            `}
                            aria-label="Fermer la notification"
                        >
                            <i className="ri-close-line text-lg" />
                        </button>
                    </div>
                )}

                {/* Effet premium */}
                {type === 'premium' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                )}
            </div>
        </div>
    );
};
export default ModernNotification;